import React from "react";
import { Printer } from "lucide-react";

/**
 * Merch mock-up sheet — A4 portrait, 6 tiles showing where the new logo
 * lives across everyday physical touchpoints. Uses CSS silhouettes so the
 * output prints crisp and stays on-brand. Perfect for merch order specs,
 * uniform briefs, and pitch decks.
 */

const LOGO = "/media/logo-300.png";

/* Each mock is a container with a product silhouette + logo placement */
function Mock({ label, subtitle, children }) {
  return (
    <div className="mock-tile">
      <div className="mock-stage">{children}</div>
      <div className="mock-caption">
        <div className="mock-label">{label}</div>
        <div className="mock-sub">{subtitle}</div>
      </div>
    </div>
  );
}

function TShirt() {
  return (
    <div className="tshirt">
      <div className="tshirt-collar" />
      <div className="tshirt-body">
        <img src={LOGO} alt="" className="tshirt-logo" />
      </div>
    </div>
  );
}

function Apron() {
  return (
    <div className="apron">
      <div className="apron-neck" />
      <div className="apron-body">
        <div className="apron-pocket" />
        <img src={LOGO} alt="" className="apron-logo" />
      </div>
    </div>
  );
}

function CoffeeCup() {
  return (
    <div className="cup">
      <div className="cup-lid" />
      <div className="cup-body">
        <div className="cup-sleeve">
          <img src={LOGO} alt="" className="cup-logo" />
        </div>
      </div>
    </div>
  );
}

function KraftBox() {
  return (
    <div className="box">
      <div className="box-top" />
      <div className="box-front">
        <img src={LOGO} alt="" className="box-logo" />
      </div>
    </div>
  );
}

function ToteBag() {
  return (
    <div className="tote">
      <div className="tote-handle tote-handle-l" />
      <div className="tote-handle tote-handle-r" />
      <div className="tote-body">
        <img src={LOGO} alt="" className="tote-logo" />
      </div>
    </div>
  );
}

function StoreSign() {
  return (
    <div className="sign">
      <div className="sign-frame">
        <img src={LOGO} alt="" className="sign-logo" />
        <div className="sign-line">MAMAMINA&apos;S SOULFEED</div>
        <div className="sign-line-sub">GOOD FOOD · GOOD LIFE</div>
      </div>
      <div className="sign-shadow" />
    </div>
  );
}

export default function PrintMerch() {
  return (
    <div className="print-shell">
      <MerchStyles />
      <header className="print-header no-print">
        <div>
          <h1>Merch mock-up sheet</h1>
          <p>Printable A4 preview of the new logo on 6 branded touchpoints — hand to your printer, embroiderer, or investors.</p>
        </div>
        <button className="print-btn" onClick={() => window.print()} data-testid="print-merch-btn">
          <Printer size={16} /> Print / Save PDF
        </button>
      </header>

      <div className="a4-page">
        {/* Header row */}
        <div className="merch-header">
          <img src={LOGO} alt="Mamamina's Soulfeed" className="merch-header-logo" />
          <div className="merch-header-text">
            <div className="merch-header-title">Brand Applications</div>
            <div className="merch-header-sub">Where the logo lives in the real world</div>
          </div>
        </div>

        <div className="merch-grid">
          <Mock label="Team T-shirt" subtitle="Chest badge · natural cotton">
            <TShirt />
          </Mock>
          <Mock label="Chef Apron" subtitle="Centred crest · charcoal denim">
            <Apron />
          </Mock>
          <Mock label="Takeaway Cup" subtitle="Sleeve print · kraft cup">
            <CoffeeCup />
          </Mock>
          <Mock label="Takeaway Box" subtitle="Lid stamp · recycled kraft">
            <KraftBox />
          </Mock>
          <Mock label="Tote Bag" subtitle="Screen print · natural canvas">
            <ToteBag />
          </Mock>
          <Mock label="Storefront Sign" subtitle="Wood plaque · antique gold">
            <StoreSign />
          </Mock>
        </div>

        {/* Palette + type strip */}
        <div className="merch-strip">
          <div className="merch-strip-block">
            <div className="merch-strip-title">Brand palette</div>
            <div className="merch-swatches">
              <div className="swatch" style={{ background: "#B7853D" }} />
              <div className="swatch" style={{ background: "#1a1512" }} />
              <div className="swatch" style={{ background: "#f6ebd0" }} />
              <div className="swatch" style={{ background: "#c1272d" }} />
              <div className="swatch" style={{ background: "#2b3a52" }} />
            </div>
            <div className="merch-swatch-labels">
              <span>Gold · #B7853D</span>
              <span>Ink · #1A1512</span>
              <span>Cream · #F6EBD0</span>
              <span>Red · #C1272D</span>
              <span>Navy · #2B3A52</span>
            </div>
          </div>
          <div className="merch-strip-block">
            <div className="merch-strip-title">Do</div>
            <ul className="merch-list">
              <li>Keep clear-space equal to the M&apos;s height</li>
              <li>Print at ≥ 25 mm width for legibility</li>
              <li>Prefer the cream card back for dark garments</li>
            </ul>
          </div>
          <div className="merch-strip-block">
            <div className="merch-strip-title">Don&apos;t</div>
            <ul className="merch-list">
              <li>Recolour the red headwrap</li>
              <li>Add drop-shadows or bevels</li>
              <li>Stretch the badge or crop the circle</li>
            </ul>
          </div>
        </div>

        <div className="merch-foot">Mamamina&apos;s Soulfeed · Brisbane · mamaminassoulfeed.online</div>
      </div>

      <footer className="no-print print-footer">
        <p>On your printer dialog: <strong>A4</strong>, <strong>100% scale</strong>, <strong>colour</strong>, <strong>background graphics ON</strong> (Chrome: More settings → Background graphics).</p>
      </footer>
    </div>
  );
}

/* ----------------------------------------------------------------- */
function MerchStyles() {
  return (
    <style>{`
      .print-shell {
        min-height: 100vh; background: #0f0b09; color: #e4dcc9;
        padding: 32px 16px 64px; font-family: -apple-system, "Segoe UI", sans-serif;
      }
      .print-header {
        max-width: 900px; margin: 0 auto 24px; display: flex;
        align-items: center; justify-content: space-between; gap: 16px;
        flex-wrap: wrap;
      }
      .print-header h1 { font-size: 22px; margin: 0 0 4px; color: #f6ebd0; }
      .print-header p { font-size: 12px; color: #a09484; margin: 0; }
      .print-btn {
        display: inline-flex; align-items: center; gap: 8px;
        background: #B7853D; color: #1a1512; border: none;
        padding: 10px 18px; font-weight: 600; cursor: pointer;
        border-radius: 4px; font-size: 13px;
      }
      .print-footer { max-width: 900px; margin: 24px auto 0; color: #a09484; font-size: 12px; }

      /* A4 page container */
      .a4-page {
        width: 210mm; min-height: 297mm; margin: 0 auto;
        background: #f6ebd0; color: #1a1512;
        padding: 12mm 12mm 15mm; box-shadow: 0 4px 24px rgba(0,0,0,.4);
        display: flex; flex-direction: column;
      }

      /* Top brand strip */
      .merch-header {
        display: flex; align-items: center; gap: 6mm;
        border-bottom: 0.3mm solid #B7853D; padding-bottom: 6mm; margin-bottom: 8mm;
      }
      .merch-header-logo { height: 22mm; width: auto; }
      .merch-header-title {
        font-family: Georgia, serif; font-style: italic;
        font-size: 8mm; color: #1a1512; line-height: 1;
      }
      .merch-header-sub {
        font-size: 3mm; letter-spacing: 1.5mm; color: #5a4d40;
        text-transform: uppercase; margin-top: 2mm;
      }

      /* 2×3 grid of mocks */
      .merch-grid {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 6mm;
        margin-bottom: 8mm;
      }
      .mock-tile {
        background: white;
        border: 0.2mm solid #d4c9b0;
        padding: 4mm;
        display: flex; flex-direction: column;
      }
      .mock-stage {
        flex: 1; display: flex; align-items: center; justify-content: center;
        min-height: 48mm; background: #ede2c7; margin-bottom: 3mm;
        position: relative; overflow: hidden;
      }
      .mock-caption { text-align: center; }
      .mock-label {
        font-family: Georgia, serif; font-size: 3.4mm;
        color: #1a1512; font-weight: 600;
      }
      .mock-sub {
        font-size: 2.4mm; color: #7a6f5f;
        letter-spacing: 0.3mm; margin-top: 0.6mm;
      }

      /* ---- Individual product silhouettes ---- */

      /* T-shirt */
      .tshirt {
        position: relative; width: 42mm; height: 42mm;
      }
      .tshirt-collar {
        position: absolute; top: 2mm; left: 50%; transform: translateX(-50%);
        width: 10mm; height: 3mm; background: #d4b895;
        border-radius: 0 0 5mm 5mm;
      }
      .tshirt-body {
        position: absolute; top: 4mm; left: 0; right: 0; bottom: 0;
        background: #f6ebd0; /* cream tee */
        clip-path: polygon(
          0 8%, 20% 0%, 30% 6%, 40% 10%, 60% 10%, 70% 6%, 80% 0%, 100% 8%,
          100% 22%, 82% 22%, 82% 100%, 18% 100%, 18% 22%, 0 22%
        );
        display: flex; justify-content: center; align-items: center;
        padding-top: 12mm;
      }
      .tshirt-logo {
        width: 14mm; height: 14mm; object-fit: contain;
      }

      /* Apron */
      .apron {
        position: relative; width: 32mm; height: 44mm;
      }
      .apron-neck {
        position: absolute; top: 0; left: 50%; transform: translateX(-50%);
        width: 12mm; height: 3mm;
        border-top: 0.5mm solid #4a3d30; border-radius: 12mm 12mm 0 0;
      }
      .apron-body {
        position: absolute; top: 3mm; left: 2mm; right: 2mm; bottom: 0;
        background: #3d2f24;
        clip-path: polygon(30% 0, 70% 0, 100% 20%, 100% 100%, 0 100%, 0 20%);
        display: flex; flex-direction: column; align-items: center; padding-top: 6mm;
      }
      .apron-pocket {
        position: absolute; bottom: 5mm; left: 5mm; right: 5mm; height: 8mm;
        border-top: 0.4mm solid #5a4d40;
      }
      .apron-logo { width: 18mm; height: 18mm; object-fit: contain; }

      /* Coffee cup */
      .cup {
        position: relative; width: 32mm; height: 42mm;
      }
      .cup-lid {
        position: absolute; top: 0; left: 50%; transform: translateX(-50%);
        width: 24mm; height: 4mm; background: #1a1512;
        border-radius: 3mm 3mm 0 0;
      }
      .cup-body {
        position: absolute; top: 4mm; left: 3mm; right: 3mm; bottom: 0;
        background: #a67a4d;
        clip-path: polygon(0 0, 100% 0, 90% 100%, 10% 100%);
        display: flex; justify-content: center; align-items: center;
      }
      .cup-sleeve {
        position: absolute; top: 12mm; bottom: 6mm; left: 2mm; right: 2mm;
        background: #5c4331; display: flex; justify-content: center; align-items: center;
      }
      .cup-logo { width: 14mm; height: 14mm; object-fit: contain; }

      /* Takeaway box */
      .box {
        position: relative; width: 44mm; height: 34mm;
      }
      .box-top {
        position: absolute; top: 0; left: 0; right: 0; height: 6mm;
        background: #8b6539;
        clip-path: polygon(5% 0, 95% 0, 100% 100%, 0 100%);
      }
      .box-front {
        position: absolute; top: 6mm; left: 0; right: 0; bottom: 0;
        background: #b58252;
        display: flex; justify-content: center; align-items: center;
      }
      .box-logo { width: 20mm; height: 20mm; object-fit: contain; }

      /* Tote bag */
      .tote {
        position: relative; width: 38mm; height: 44mm;
      }
      .tote-handle {
        position: absolute; top: 0; width: 3mm; height: 10mm;
        border: 0.6mm solid #4a3d30; border-bottom: none;
        border-radius: 3mm 3mm 0 0;
      }
      .tote-handle-l { left: 8mm; }
      .tote-handle-r { right: 8mm; }
      .tote-body {
        position: absolute; top: 8mm; left: 0; right: 0; bottom: 0;
        background: #e6d3ac;
        display: flex; justify-content: center; align-items: center;
        border-top: 0.4mm solid #b8a480;
      }
      .tote-logo { width: 18mm; height: 18mm; object-fit: contain; }

      /* Storefront sign */
      .sign {
        position: relative; width: 46mm; height: 42mm;
      }
      .sign-frame {
        position: absolute; top: 3mm; left: 3mm; right: 3mm; bottom: 6mm;
        background: #1a1512;
        border: 0.4mm solid #B7853D;
        display: flex; flex-direction: column; align-items: center;
        justify-content: center; padding: 2mm;
      }
      .sign-logo {
        width: 14mm; height: 14mm; object-fit: contain;
        filter: brightness(0) invert(1);   /* silhouette-only for print consistency */
        opacity: 0;                         /* we'll skip the inline logo, use the wordmark instead */
      }
      .sign-line {
        font-family: Georgia, serif; font-size: 3.4mm;
        color: #B7853D; letter-spacing: 0.6mm; text-align: center;
      }
      .sign-line-sub {
        font-size: 1.8mm; letter-spacing: 1mm; color: #e4dcc9;
        text-align: center; margin-top: 1mm;
      }
      .sign-shadow {
        position: absolute; top: 3mm; left: 5mm; right: 5mm; bottom: 4mm;
        background: rgba(0,0,0,0.15); z-index: -1;
        transform: translate(1mm, 1mm);
      }

      /* Bottom info strip */
      .merch-strip {
        display: grid; grid-template-columns: 2fr 1fr 1fr;
        gap: 6mm; padding-top: 6mm; border-top: 0.3mm solid #B7853D;
        margin-top: auto;
      }
      .merch-strip-title {
        font-size: 2.5mm; letter-spacing: 0.8mm; color: #a09484;
        text-transform: uppercase; margin-bottom: 2mm;
      }
      .merch-strip-block { font-size: 2.6mm; color: #4a3d30; line-height: 1.5; }
      .merch-list { margin: 0; padding-left: 3mm; }
      .merch-list li { margin-bottom: 1mm; }
      .merch-swatches {
        display: flex; gap: 1.5mm; margin-bottom: 1.5mm;
      }
      .swatch {
        width: 8mm; height: 8mm; border-radius: 1mm;
        border: 0.15mm solid rgba(0,0,0,0.1);
      }
      .merch-swatch-labels {
        display: flex; gap: 2mm; flex-wrap: wrap;
        font-size: 2mm; color: #5a4d40; letter-spacing: 0.2mm;
      }

      .merch-foot {
        margin-top: 6mm; padding-top: 4mm;
        border-top: 0.2mm solid #d4c9b0;
        text-align: center; font-size: 2.2mm;
        letter-spacing: 1mm; color: #7a6f5f; text-transform: uppercase;
      }

      @page { size: A4; margin: 0; }
      @media print {
        body, html { background: white !important; margin: 0; }
        .print-shell { background: white; padding: 0; }
        .no-print { display: none !important; }
        .a4-page { box-shadow: none; margin: 0; }
        .a4-page, .mock-stage, .tshirt-body, .apron-body, .cup-body,
        .cup-sleeve, .box-front, .box-top, .tote-body, .sign-frame,
        .cup-lid {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `}</style>
  );
}
